import { Box, Button, FormControlLabel, Grid, Pagination, Stack, Switch, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { confirmAlert } from "react-confirm-alert";
import { ApiError } from "../../Api/ApiError";
import { AllStaffList, DeleteStaff } from "../../Api/Staff";
import { StaffData } from "../../Objects/StaffData";
import PageTitle from "../atoms/PageTitle";
import StaffCard from "../molecules/StaffCard";
import StaffEditDialog from "../organisms/StaffEditDialog";

const StaffPage: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar()

    const [staffList, setStaffList] = React.useState<StaffData[]>()
    const [rows, setRows] = React.useState<number>()
    const [page, setPage] = React.useState<number>(1)
    const [limit] = React.useState<number>(10)
    const [showDeleted, setShowDeleted] = React.useState<boolean>(false)

    const [editStaffId, setEditStaffId] = React.useState<number>()

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const loadStaffList = React.useCallback(() => {
        AllStaffList({
            page: page,
            limit: limit,
            show_deleted: showDeleted
        }, {signal: abortController.signal}).then(result => {
            setStaffList(result.list)
            setRows(result.rows)
        }).catch((e: any) => {
            if( e.__CANCEL__ ) {
                console.info("canceled")
            } else if (e instanceof ApiError) {
                enqueueSnackbar(e.message, { variant: "error" })
            } else if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: "error" })
            }else {
                enqueueSnackbar("通信に失敗しました", { variant: "error" })
            }
        })
    }, [abortController, page, limit, showDeleted, enqueueSnackbar])

    const doDelete = React.useCallback((staffData: StaffData) => {
        if (staffData.stf_id) {
            confirmAlert({
                title: "削除しますか？",
                message: "削除を取り消すことはできません。ご注意ください。",
                buttons: [
                    {
                        label: "削除する",
                        onClick: () => {
                            if (staffData.stf_id) {
                                DeleteStaff({
                                    stf_id: staffData.stf_id
                                }, {signal: abortController.signal}).then(() => {
                                    enqueueSnackbar("削除しました", { variant: "success" })
                                }).catch((e: any) => {
                                    if( e.__CANCEL__ ) {
                                        console.info("canceled")
                                    } else if (e instanceof ApiError) {
                                        enqueueSnackbar(e.message, { variant: "error" })
                                    } else if (e instanceof Error) {
                                        enqueueSnackbar(e.message, { variant: "error" })
                                    }else {
                                        enqueueSnackbar("通信に失敗しました", { variant: "error" })
                                    }
                                }).finally(() => {
                                    loadStaffList()
                                })
                            }
                        }
                    }, {
                        label: "キャンセル",
                        onClick: () => { }
                    }
                ]
            })
        }
    }, [abortController, loadStaffList, enqueueSnackbar])

    React.useEffect(() => {
        loadStaffList()
    }, [loadStaffList])

    return (
        <>
            <PageTitle text="スタッフ管理" />
            <Stack
                direction={"row"}
                justifyContent={"right"}
                my={2}
            >
                <Button
                    variant="contained"
                    onClick={() => {
                        setEditStaffId(0)
                    }}
                >新規スタッフ登録</Button>
            </Stack>
            {staffList ? (
                <Stack
                    spacing={2}
                >
                    {rows &&
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "right",
                            }}
                        >
                            <Pagination
                                count={Math.ceil(rows / limit)}
                                page={page}
                                variant="outlined"
                                onChange={(e, page) => {
                                    setPage(page)
                                }}
                            />
                        </Box>
                    }
                    <FormControlLabel
                        control={<Switch
                            checked={showDeleted}
                            onChange={(e) => {
                                setShowDeleted(e.target.checked)
                                setPage(1)
                            }}
                        />}
                        label="抹消済みのスタッフも表示する"
                    />
                    <Box>
                        <Grid container spacing={2} alignItems="stretch">
                            {staffList.map(staffData => (
                                <Grid item key={staffData.stf_id} xs={12} sm={6}>
                                    <StaffCard
                                        staffData={staffData}
                                        onEdit={(staffData) => {
                                            setEditStaffId(staffData.stf_id)
                                        }}
                                        onDelete={(staffData) => {
                                            doDelete(staffData)
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                    {rows &&
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <Pagination
                                count={Math.ceil(rows / limit)}
                                page={page}
                                variant="outlined"
                                onChange={(e, page) => {
                                    setPage(page)
                                }}
                            />
                        </Box>
                    }
                </Stack>
            ) : (
                <Typography>loading...</Typography>
            )}
            <StaffEditDialog
                stfId={editStaffId}
                onClose={() => {
                    setEditStaffId(undefined)
                    loadStaffList()
                }}
            />
        </>
    )
}

export default StaffPage